import { type FC, useMemo, useState } from "react";

import { ChevronRight, FileText, UsersRound } from "lucide-react";
import { Link } from "react-router-dom";

import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { JourneyProgressBar } from "@/shared/components/JourneyProgressBar";
import { getModuleById } from "@/shared/constants/estateEssentials";
import { usePerson, useUser } from "@/shared/hooks/useAppContext";
import { useModuleProgress } from "@/shared/hooks/useModuleProgress";
import { usePaymentTier } from "@/shared/hooks/usePaymentTier";
import { ASSET_SEARCH_TASK_ID, isAssetSearchSubmitted } from "@/shared/constants/assetSearch";
import { ModuleCard } from "@/pages/postloss/estate-essentials/components/ModuleCard";
import PostLossPaywall from "@/pages/postloss/components/PostLossPaywall";

import type { LegalRole } from "@/shared/hooks/useUserEstateRole";
import type { HomeStateConfig } from "../resolveHomeState";
import type { AuthorityPath } from "@/shared/utils/authorityPath";

import { SetupHeroCard } from "./SetupHeroCard";
import { AuthorityHeroCard } from "./AuthorityHeroCard";
import { ActivitySection } from "./ActivitySection";
import { AssetsDebtsWidget } from "./AssetsDebtsWidget";
import { EstateExpertWidget } from "./EstateExpertWidget";
import { CommonQuestionsWidget } from "./CommonQuestionsWidget";
import { QuickActionsWidget } from "./QuickActionsWidget";
import {
  buildPreAuthorityQuestions,
  buildLettersAuthorityQuestions,
  buildTrustAuthorityQuestions,
  buildSomeoneElseQuestions,
} from "./commonQuestions";

interface HomeViewProps {
  config: HomeStateConfig;
  personName: string;
  nomineeName?: string;
  legalRole: LegalRole;
  hasInitialInfo: boolean;
  hasDeathCert: boolean;
  hasLegalRole: boolean;
  authorityPath?: AuthorityPath | null;
  hasCompletedAuthorityModal?: boolean;
  contactCount: number;
  keyDocCount: number;
  keyDocTotal: number;
  isJourneyPaused?: boolean;
  onFinishSetup: () => void;
  onAuthorityCtaClick: () => void;
}

const TODO_MODULE_IDS = ["notify-credit-bureaus", "do-not-mail"] as const;

export const HomeView: FC<HomeViewProps> = ({
  config,
  personName,
  nomineeName,
  legalRole,
  hasInitialInfo,
  hasDeathCert,
  hasLegalRole,
  authorityPath,
  hasCompletedAuthorityModal,
  contactCount,
  keyDocCount,
  keyDocTotal,
  isJourneyPaused = false,
  onFinishSetup,
  onAuthorityCtaClick,
}) => {
  const user = useUser();
  const person = usePerson();
  const estateId = person?.estate_id;
  const firstName = person?.first_name || undefined;
  const { taskStatusMap } = useModuleProgress();
  const isSearchRunning = isAssetSearchSubmitted(taskStatusMap.get(ASSET_SEARCH_TASK_ID));
  const { tier } = usePaymentTier(estateId);
  const [showPaywall, setShowPaywall] = useState(false);

  const completedTaskIds = useMemo(() => {
    const ids = new Set<string>();
    for (const [taskId, status] of taskStatusMap) {
      if (status === "completed") ids.add(taskId);
    }
    return ids;
  }, [taskStatusMap]);

  const todoModules = TODO_MODULE_IDS.map((id) => getModuleById(id)).filter(
    (m): m is NonNullable<typeof m> => !!m,
  );

  const questions = useMemo(() => {
    if (config.state === "has-authority" || config.state === "settle-estate") {
      if (legalRole === "trustee") {
        return buildTrustAuthorityQuestions(personName);
      }
      return buildLettersAuthorityQuestions(personName);
    }
    if (config.state === "someone-else-authority") {
      return buildSomeoneElseQuestions(nomineeName ?? "the executor");
    }
    return buildPreAuthorityQuestions();
  }, [config.state, legalRole, personName, nomineeName]);

  const renderHeroCard = () => {
    if (config.state === "setup-incomplete") {
      const setupSteps = [
        { label: `Basic details about ${personName}`, completed: hasInitialInfo },
        { label: `${personName}'s death certificate`, completed: hasDeathCert },
        { label: "Who can act for the estate", completed: hasLegalRole },
      ];
      const remainingCount = setupSteps.filter((s) => !s.completed).length;

      return (
        <SetupHeroCard
          steps={setupSteps}
          remainingCount={remainingCount}
          onFinishSetup={onFinishSetup}
        />
      );
    }

    return (
      <AuthorityHeroCard
        state={config.state}
        personName={personName}
        nomineeName={nomineeName}
        authorityPath={authorityPath}
        hasCompletedAuthorityModal={hasCompletedAuthorityModal}
        isSearchRunning={isSearchRunning}
        onCtaClick={onAuthorityCtaClick}
      />
    );
  };

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-7">
        <h2>
          {config.step >= 3 || config.state === "someone-else-authority"
            ? `Welcome, ${user?.first_name || ""}`
            : `${personName}'s estate`}
        </h2>
        <JourneyProgressBar steps={config.journeySteps} paused={isJourneyPaused} />
      </div>

      {renderHeroCard()}

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex flex-col gap-8 lg:w-[66%] min-w-0">
          <ActivitySection />

          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <p className="text-base font-semibold tracking-[-0.02em]">Important to-dos</p>
              <Link
                to={POST_LOSS_ROUTES.ESTATE_ESSENTIALS}
                className="text-sm text-brand no-underline hover:underline tracking-[-0.02em]"
              >
                View all
              </Link>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              {todoModules.map((module) => (
                <ModuleCard
                  key={module.id}
                  module={module}
                  firstName={firstName}
                  isComplete={!!module.taskId && completedTaskIds.has(module.taskId)}
                  className="flex-1 min-w-0"
                />
              ))}
            </div>
          </div>

          <AssetsDebtsWidget
            personName={personName}
            hasAuthority={config.state === "has-authority" || config.state === "settle-estate"}
          />

          <div className="flex flex-col sm:flex-row gap-4">
            <StatCard
              icon={UsersRound}
              iconBg="bg-blue-100"
              iconColor="text-blue-600"
              title={`${contactCount} contacts`}
              description={`Family, professionals, and others involved in ${personName}'s estate`}
              to={POST_LOSS_ROUTES.CONTACTS}
            />
            <StatCard
              icon={FileText}
              iconBg="bg-purple-100"
              iconColor="text-purple-600"
              title={`${keyDocCount} of ${keyDocTotal} key documents uploaded`}
              description="The documents you'll need most often"
              to={POST_LOSS_ROUTES.DOCUMENTS}
            />
          </div>
        </div>

        <div className="flex flex-col gap-8 lg:flex-1 min-w-0">
          <EstateExpertWidget
            onSeePlans={() => setShowPaywall(true)}
          />
          <CommonQuestionsWidget questions={questions} />
          <QuickActionsWidget />
        </div>
      </div>
      <PostLossPaywall
        isOpen={showPaywall}
        onOpenChange={setShowPaywall}
        promptLocation="home_see_plans"
        currentTier={tier}
      />
    </div>
  );
};

const StatCard: FC<{
  icon: FC<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  title: string;
  description: string;
  to: string;
}> = ({ icon: Icon, iconBg, iconColor, title, description, to }) => (
  <Link
    to={to}
    className="flex flex-1 flex-col gap-3 border border-primary rounded-lg bg-white p-6 no-underline min-w-0 hover:border-strong transition-colors"
  >
    <div className="flex items-center gap-3">
      <div className={`size-8 rounded ${iconBg} flex items-center justify-center shrink-0`}>
        <Icon className={`size-4 ${iconColor}`} />
      </div>
      <p className="flex-1 text-base font-semibold tracking-[-0.02em] min-w-0">{title}</p>
      <ChevronRight className="size-4 text-secondary shrink-0" />
    </div>
    <p className="text-sm text-secondary tracking-[-0.02em]">{description}</p>
  </Link>
);
